import { useEffect, useState, useMemo } from "react";
import usePrinterQueue from "../../hooks/usePrinterQueue";
import styles from "./styles.module.css";
import { Button, IconButton } from "@mui/material";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import { ReactComponent as DeleteIcon } from "images/svg/delete-02.svg";

const formatQueueName = (key = "") => {
  const parts = key.split("_");
  if (parts.length >= 2) {
    return `${parts[0]} - ${parts.slice(1).join("_")}`;
  }
  return key;
};

const getStatusBadgeClass = (status = "") => {
  const s = String(status).toLowerCase();
  if (s === "failed" || s === "error") return styles.statusFailed;
  if (s === "processing" || s === "running") return styles.statusProcessing;
  if (s === "pending" || s === "waiting") return styles.statusPending;
  if (s === "completed" || s === "success") return styles.statusSuccess;
  return styles.statusDefault;
};

const PrinterQueuePage = () => {
  const { queues, clearAllQueue, stopAllQueues, startAllQueues, clearPrintJob } = usePrinterQueue();
  const [selectedQueue, setSelectedQueue] = useState("");

  const targetKeys = useMemo(() => (queues ? Array.from(queues.keys()) : []), [queues]);
  const selectedQueueJobs = useMemo(() => (queues && queues.get(selectedQueue)) || [], [queues, selectedQueue]);

  useEffect(() => {
    if (!selectedQueue && targetKeys.length > 0) {
      setSelectedQueue(targetKeys[0]);
    } else if (selectedQueue && targetKeys.length > 0 && !targetKeys.includes(selectedQueue)) {
      setSelectedQueue(targetKeys[0]);
    } else if (targetKeys.length === 0 && selectedQueue) {
      setSelectedQueue("");
    }
  }, [targetKeys, selectedQueue]);

  return (
    <div className={styles.printerQueuePageWrapper}>
      {!queues || targetKeys.length === 0 ? (
        <div className={styles.emptyQueueContainer}>
          <p>No printer queues found</p>
        </div>
      ) : (
        <>
          <div className={styles.queueSelectContainer}>
            {targetKeys.map((targetKey) => {
              const isSelected = selectedQueue === targetKey;
              return (
                <button
                  key={targetKey}
                  type="button"
                  className={`${styles.queueSelectButton} ${isSelected ? styles.queueSelectButtonActive : ""}`}
                  onClick={() => setSelectedQueue(targetKey)}
                >
                  <span>{formatQueueName(targetKey)}</span>
                </button>
              );
            })}
          </div>

          <div className={styles.queueContentArea}>
            <div className={styles.queueTableWrapper}>
              <table className={styles.queueTable}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Label</th>
                    <th>Status</th>
                    <th>Retry Count</th>
                    <th>Time</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedQueueJobs?.length ? (
                    selectedQueueJobs.map((item) => (
                      <tr key={item?.id}>
                        <td className={styles.queueIdCell}>{item?.id ?? "-"}</td>
                        <td>{item?.label || "-"}</td>
                        <td>
                          <span className={`${styles.queueStatusBadge} ${getStatusBadgeClass(item?.status)}`}>
                            {item?.status || "-"}
                          </span>
                        </td>
                        <td>{item?.retryCount ?? 0}</td>
                        <td>{DateTimeHelpers.formatDateAndTime(item?.createdAt)}</td>
                        <td>
                          <IconButton size="small" onClick={() => clearPrintJob(item?.id)} style={{ color: "#7c7c7d" }}>
                            <DeleteIcon className={styles.deleteIcon} />
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className={styles.emptyRowCell}>
                        No pending jobs in this queue
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {queues && targetKeys.length > 0 && (
        <div className={styles.queueBottomActions}>
          <Button className={styles.buttonRed} onClick={stopAllQueues}>
            Stop All Queues
          </Button>
          <Button className={styles.buttonRed} onClick={startAllQueues}>
            Start All Queues
          </Button>
          <Button className={styles.buttonRed} onClick={clearAllQueue}>
            Clear All Queues
          </Button>
        </div>
      )}
    </div>
  );
};

export default PrinterQueuePage;
